import { getPushNotificationsPublicKey, subscribePushNotifications, unsubscribePushNotifications } from '@api/pushNotifications'
import { reportUnexpectedClientError } from './client-error.service'


export type PushNotificationStatus = 'unsupported' | 'denied' | 'default' | 'disabled' | 'enabled'

export type PushNotificationPayload = {
    applicationID?: number
    title?: string
    body?: string
    icon?: string
    badge?: string
    url?: string
    tag?: string
    soundUrl?: string
}

export type PushNotificationState = {
    status: PushNotificationStatus
    isEnabled: boolean
    message: string
}

type PushNotificationActionResult = PushNotificationState & {
    isError?: boolean
}

const SERVICE_WORKER_PATH = '/crm-push-sw.js'
const FALLBACK_NOTIFICATION_DELAY_MS = 800
const DEFAULT_NOTIFICATION_PATH = '/lk/applications'


class PushNotificationsService {
    /** Синхронизирует уже включённую подписку с сервером без показа prompt. */
    syncCurrentSubscription = async (): Promise<PushNotificationState> => {
        if (!this.isSupported()) return this.getUnsupportedState()

        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return this.getLocalOriginState()
        }

        if (Notification.permission === 'denied') return this.getDeniedState()
        if (Notification.permission !== 'granted') {
            return {
                status: 'default',
                isEnabled: false,
                message: 'Уведомления о новых заявках выключены.',
            }
        }

        return this.createOrSyncSubscription()
    }

    /** Показывает OS-уведомление из открытой CRM, если service worker не показал своё. */
    showApplicationNotificationFallback = async (payload: PushNotificationPayload = {}) => {
        if (!this.isSupported()) return
        if (this.isLocalOrigin()) return
        if (Notification.permission !== 'granted') return

        const tag = payload.tag || this.getApplicationNotificationTag(payload.applicationID)
        const notificationPath = this.getSafeNotificationPath(
            payload.url,
            payload.applicationID ? `/lk/applications/${payload.applicationID}` : DEFAULT_NOTIFICATION_PATH,
        )

        await this.delay(FALLBACK_NOTIFICATION_DELAY_MS)

        const registration = await navigator.serviceWorker.getRegistration()
        const existsNotifications = await this.getShownNotifications(registration, tag)
        if (existsNotifications.length > 0) return

        const notificationOptions: NotificationOptions = {
            body: payload.body || 'Новая заявка в CRM.',
            icon: payload.icon || '/logo192.png',
            badge: payload.badge || '/logo192.png',
            data: {
                url: notificationPath,
                applicationID: payload.applicationID,
            },
            tag,
            renotify: true,
            silent: false,
        }

        if (registration?.showNotification) {
            await registration.showNotification(payload.title || 'ЦРМ Система', notificationOptions)
            return
        }

        const notification = new Notification(payload.title || 'ЦРМ Система', notificationOptions)
        notification.onclick = (event) => {
            event.preventDefault()
            notification.close()
            window.focus()
            window.location.assign(notificationPath)
        }
    }

    /** Проверяет, может ли текущий браузер использовать Web Push. */
    getStatus = async (): Promise<PushNotificationState> => {
        if (!this.isSupported()) return this.getUnsupportedState()
        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return this.getLocalOriginState()
        }
        if (Notification.permission === 'denied') return this.getDeniedState()

        if (Notification.permission === 'granted') return this.createOrSyncSubscription()

        return {
            status: 'default',
            isEnabled: false,
            message: 'Уведомления о новых заявках выключены.',
        }
    }

    /** Запрашивает разрешение, создаёт подписку браузера и сохраняет её на сервере. */
    enable = async (): Promise<PushNotificationActionResult> => {
        if (!this.isSupported()) return { ...this.getUnsupportedState(), isError: true }
        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return { ...this.getLocalOriginState(), isError: true }
        }
        if (Notification.permission === 'denied') return { ...this.getDeniedState(), isError: true }

        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
            return { status: 'default', isEnabled: false, isError: true, message: 'Разрешение на уведомления не выдано.' }
        }

        return this.createOrSyncSubscription()
    }

    /** Отключает подписку в браузере и помечает её неактивной на сервере. */
    disable = async (): Promise<PushNotificationActionResult> => {
        if (!this.isSupported()) return { ...this.getUnsupportedState(), isError: true }

        const registration = await navigator.serviceWorker.getRegistration()
        const subscription = await registration?.pushManager.getSubscription()

        if (!subscription) {
            return { status: 'disabled', isEnabled: false, message: 'Уведомления о новых заявках уже выключены.' }
        }

        const endpoint = subscription.endpoint
        await subscription.unsubscribe()

        const unsubscribeResponse = await unsubscribePushNotifications(endpoint)
        if (unsubscribeResponse.status !== 200) {
            return { status: 'disabled', isEnabled: false, isError: true, message: unsubscribeResponse.message || 'Подписка отключена в браузере, но сервер не подтвердил отключение.' }
        }

        return { status: 'disabled', isEnabled: false, message: 'Уведомления о новых заявках выключены.' }
    }

    private isSupported = () => typeof window !== 'undefined'
        && window.isSecureContext
        && 'Notification' in window
        && 'serviceWorker' in navigator
        && 'PushManager' in window

    private isLocalOrigin = () => ['localhost', '127.0.0.1'].includes(window.location.hostname)

    private getSafeNotificationPath = (value: string | undefined, fallbackPath: string) => {
        try {
            const targetUrl = new URL(value || fallbackPath, window.location.origin)
            if (targetUrl.origin !== window.location.origin) return fallbackPath

            return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
        } catch {
            return fallbackPath
        }
    }

    private unsubscribeCurrentBrowserSubscription = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration()
            const subscription = await registration?.pushManager.getSubscription()

            if (!subscription) return

            const endpoint = subscription.endpoint
            await subscription.unsubscribe()
            await unsubscribePushNotifications(endpoint)
        } catch (error) {
            reportUnexpectedClientError(error, 'PushNotifications.unsubscribeCurrentBrowserSubscription')
            return
        }
    }

    private syncSubscription = async (subscription: PushSubscription) => {
        try {
            await subscribePushNotifications(subscription.toJSON(), window.location.origin)
        } catch (error) {
            reportUnexpectedClientError(error, 'PushNotifications.syncSubscription')
            return
        }
    }

    private createOrSyncSubscription = async (): Promise<PushNotificationActionResult> => {
        try {
            const publicKeyResponse = await getPushNotificationsPublicKey()
            if (publicKeyResponse.status !== 200 || !publicKeyResponse.data?.publicKey) {
                return { status: 'disabled', isEnabled: false, isError: true, message: publicKeyResponse.message || 'Не удалось получить ключ уведомлений.' }
            }

            const registration = await this.getRegisteredServiceWorker()
            const subscription = await this.getOrCreateSubscription(registration, publicKeyResponse.data.publicKey)
            const subscribeResponse = await subscribePushNotifications(subscription.toJSON(), window.location.origin)

            if (subscribeResponse.status !== 200) {
                return { status: 'disabled', isEnabled: false, isError: true, message: subscribeResponse.message || 'Не удалось сохранить подписку уведомлений.' }
            }

            return { status: 'enabled', isEnabled: true, message: 'Уведомления о новых заявках включены.' }
        } catch (error) {
            const isExpectedPermissionFailure = error instanceof DOMException
                && ['AbortError', 'NotAllowedError'].includes(error.name)
            if (!isExpectedPermissionFailure) {
                reportUnexpectedClientError(error, 'PushNotifications.createOrSyncSubscription')
            }
            return { status: 'disabled', isEnabled: false, isError: true, message: 'Не удалось включить уведомления о новых заявках.' }
        }
    }

    private getUnsupportedState = (): PushNotificationState => ({
        status: 'unsupported',
        isEnabled: false,
        message: 'Браузер или текущее подключение не поддерживает Web Push. Нужен HTTPS или localhost.',
    })

    private getLocalOriginState = (): PushNotificationState => ({
        status: 'unsupported',
        isEnabled: false,
        message: 'Push-уведомления включаются только с рабочего адреса CRM.',
    })

    private getDeniedState = (): PushNotificationState => ({
        status: 'denied',
        isEnabled: false,
        message: 'Уведомления заблокированы в настройках сайта браузера.',
    })

    private registerServiceWorker = async () => navigator.serviceWorker.register(SERVICE_WORKER_PATH)

    private getRegisteredServiceWorker = async () => {
        const existsRegistration = await navigator.serviceWorker.getRegistration()
        const registration = existsRegistration || await this.registerServiceWorker()
        return this.updateServiceWorkerRegistration(registration)
    }

    private updateServiceWorkerRegistration = async (registration: ServiceWorkerRegistration) => {
        try {
            return await registration.update()
        } catch {
            return registration
        }
    }

    private getApplicationNotificationTag = (applicationID?: number) => `crm-application-${applicationID || Date.now()}`

    private getShownNotifications = async (registration: ServiceWorkerRegistration | undefined, tag: string) => {
        try {
            if (!registration?.getNotifications) return []
            return registration.getNotifications({ tag })
        } catch {
            return []
        }
    }

    private delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    private getOrCreateSubscription = async (registration: ServiceWorkerRegistration, publicKey: string) => {
        const existsSubscription = await registration.pushManager.getSubscription()
        if (existsSubscription) return existsSubscription

        return registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(publicKey),
        })
    }

    private urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)

        for (let index = 0; index < rawData.length; index += 1) {
            outputArray[index] = rawData.charCodeAt(index)
        }

        return outputArray
    }
}

const pushNotificationsService = new PushNotificationsService()

export default pushNotificationsService